/**
 * @fileoverview Modal for generating complete news section with all blurbs
 */

import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, SparklesIcon, CheckIcon, PencilIcon } from '@heroicons/react/24/outline';
import ToneSelector from './ToneSelector';

export default function NewsSectionGeneratorModal({
  isOpen,
  onClose,
  items = [],
  onGenerate,
  onApplyAll,
  onApplySingle,
  isGenerating = false,
  generatedBlurbs = [],
  sessionId = null,
  error = null,
}) {
  const [itemTones, setItemTones] = useState({});
  const [editingBlurbIndex, setEditingBlurbIndex] = useState(null);
  const [editedBlurbs, setEditedBlurbs] = useState({});

  const handleToneChange = (itemHash, tone) => {
    setItemTones((prev) => ({ ...prev, [itemHash]: tone }));
  };

  const handleGenerate = () => {
    const itemsWithTones = items.map((item) => ({
      itemHash: item.hash,
      tone: itemTones[item.hash] || 'neutral',
      selectedKeyPoints: [], // Could be extended to support key points per item
    }));
    onGenerate(itemsWithTones);
  };

  const handleEditBlurb = (index) => {
    setEditingBlurbIndex(index);
    if (!editedBlurbs[index]) {
      setEditedBlurbs((prev) => ({
        ...prev,
        [index]: generatedBlurbs[index]?.blurb || '',
      }));
    }
  };

  const handleSaveEdit = (index) => {
    setEditingBlurbIndex(null);
  };

  const handleApplySingle = (index) => {
    const blurb = editedBlurbs[index] || generatedBlurbs[index]?.blurb;
    const itemHash = generatedBlurbs[index]?.itemHash;
    if (blurb && itemHash && onApplySingle) {
      onApplySingle(itemHash, blurb);
    }
  };

  const handleApplyAll = () => {
    if (onApplyAll) {
      const blurbsToApply = generatedBlurbs.map((b, index) => ({
        itemHash: b.itemHash,
        blurb: editedBlurbs[index] || b.blurb,
      }));
      onApplyAll(blurbsToApply);
    }
  };

  const handleClose = () => {
    setEditingBlurbIndex(null);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <Dialog.Title className="flex items-center gap-2 text-base font-medium text-gray-900 dark:text-gray-100">
                    <SparklesIcon className="h-5 w-5 text-indigo-500" />
                    Generate News Section
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-4 py-4 max-h-[60vh] overflow-y-auto">
                  {/* Error State */}
                  {error && (
                    <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4 mb-4">
                      <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                    </div>
                  )}

                  {/* Pre-generation: Item list with tone selectors */}
                  {generatedBlurbs.length === 0 && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                        Set the tone for each item, then click &quot;Generate Section&quot; to create blurbs for all items at once.
                      </p>

                      {items.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                          No news items available for generation.
                        </p>
                      ) : (
                        items.map((item, index) => (
                          <div
                            key={item.hash}
                            className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                          >
                            <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200 text-xs font-medium rounded-full">
                              {index + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {item.title || 'Untitled'}
                              </p>
                              {item.summary && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                                  {item.summary}
                                </p>
                              )}
                            </div>
                            <ToneSelector
                              value={itemTones[item.hash] || 'neutral'}
                              onChange={(tone) => handleToneChange(item.hash, tone)}
                              disabled={isGenerating}
                            />
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Post-generation: Results view */}
                  {generatedBlurbs.length > 0 && (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                        Review the generated blurbs below. You can edit individual blurbs or apply all at once.
                      </p>

                      {generatedBlurbs.map((result, index) => {
                        const item = items.find((i) => i.hash === result.itemHash);
                        const isEditing = editingBlurbIndex === index;
                        const currentBlurb = editedBlurbs[index] || result.blurb;

                        return (
                          <div
                            key={result.itemHash}
                            className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200 text-xs font-medium rounded-full">
                                  {index + 1}
                                </span>
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {item?.title || 'Untitled'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                {!isEditing && (
                                  <button
                                    type="button"
                                    onClick={() => handleEditBlurb(index)}
                                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                    title="Edit blurb"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleApplySingle(index)}
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
                                >
                                  <CheckIcon className="h-3.5 w-3.5" />
                                  Apply
                                </button>
                              </div>
                            </div>

                            {isEditing ? (
                              <div className="space-y-2">
                                <textarea
                                  value={currentBlurb}
                                  onChange={(e) =>
                                    setEditedBlurbs((prev) => ({
                                      ...prev,
                                      [index]: e.target.value,
                                    }))
                                  }
                                  rows={3}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
                                />
                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleSaveEdit(index)}
                                    className="px-2 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
                                  >
                                    Done Editing
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                                {currentBlurb}
                              </p>
                            )}

                            {result.transition && (
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                Transition: {result.transition}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Loading State */}
                  {isGenerating && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Generating blurbs for {items.length} items...
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {generatedBlurbs.length > 0 ? 'Close' : 'Cancel'}
                  </button>

                  {generatedBlurbs.length === 0 ? (
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={isGenerating || items.length === 0}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGenerating ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="h-3.5 w-3.5" />
                          Generate Section
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleApplyAll}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                    >
                      <CheckIcon className="h-3.5 w-3.5" />
                      Apply All Blurbs
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
